import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr, API_BASE, getStoredToken } from "@/lib/api";
import { num } from "@/lib/format";
import {
  Ruler, Plus, Trash2, Loader2, ArrowRightToLine, Map, List, Upload, MapPin, X,
  Minus, Square, Crosshair, Camera, ImagePlus,
} from "lucide-react";

const TYPES = [
  { id: "count", label: "Count", unit: "ea" },
  { id: "length", label: "Length", unit: "lf" },
  { id: "area", label: "Area", unit: "sf" },
  { id: "volume", label: "Volume", unit: "cy" },
];

const fileUrl = (path) => `${API_BASE}/files/${path}?auth=${encodeURIComponent(getStoredToken())}`;
const priced = (it) => (Number(it.quantity) || 0) * (1 + (Number(it.waste_pct) || 0) / 100);
const lenUnit = (u) => (u === "ft" ? "lf" : u === "m" ? "m" : u || "lf");
const areaUnit = (u) => (u === "ft" ? "sf" : u === "m" ? "m2" : (u || "ft") + "2");

const WASTE_PRESETS = [
  { kw: ["tile", "tiling", "porcelain", "ceramic", "mosaic", "grout"], pct: 10 },
  { kw: ["drywall", "gypsum", "sheetrock", "partition"], pct: 5 },
  { kw: ["paint", "primer", "coating"], pct: 8 },
  { kw: ["floor", "hardwood", "laminate", "vinyl", "lvt", "carpet"], pct: 8 },
  { kw: ["concrete", "slab", "screed"], pct: 5 },
  { kw: ["brick", "block", "masonry", "cmu"], pct: 5 },
  { kw: ["insulation", "batt"], pct: 10 },
  { kw: ["roof", "shingle", "membrane"], pct: 10 },
  { kw: ["lumber", "framing", "stud", "timber", "joist"], pct: 10 },
  { kw: ["rebar", "steel", "reinforcement"], pct: 3 },
  { kw: ["pipe", "plumbing", "conduit", "wire", "cable", "electrical"], pct: 5 },
];
const suggestWaste = (desc) => {
  const d = (desc || "").toLowerCase();
  for (const p of WASTE_PRESETS) if (p.kw.some((k) => d.includes(k))) return p.pct;
  return null;
};

export default function TakeoffTab({ projectId }) {
  const qc = useQueryClient();
  const [view, setView] = useState("list");
  const [form, setForm] = useState({ description: "", measure_type: "count", quantity: "", unit: "ea", waste_pct: "", notes: "" });
  const [pushing, setPushing] = useState(false);

  const { data: items = [], isLoading } = useQuery({ queryKey: ["takeoffs", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/takeoffs`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["takeoffs", projectId] });

  const setType = (t) => setForm((f) => ({ ...f, measure_type: t, unit: TYPES.find((x) => x.id === t)?.unit || f.unit }));
  const onDesc = (v) => setForm((f) => {
    const w = f.waste_pct === "" && suggestWaste(v) != null ? String(suggestWaste(v)) : f.waste_pct;
    return { ...f, description: v, waste_pct: w };
  });

  const add = async () => {
    if (!form.description.trim()) { toast.error("Enter a description"); return; }
    try { await api.post(`/projects/${projectId}/takeoffs`, { ...form, quantity: Number(form.quantity) || 0, waste_pct: Number(form.waste_pct) || 0 }); refresh(); setForm({ ...form, description: "", quantity: "", waste_pct: "", notes: "" }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/takeoffs/${id}`); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const push = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/takeoffs/push-to-boq`);
      qc.invalidateQueries({ queryKey: ["boq", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(`Pushed ${data.added} item(s) to the estimate`);
    } catch (e) { toast.error(apiErr(e)); } finally { setPushing(false); }
  };

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><Ruler className="h-4 w-4 text-primary" /> Quantity Takeoff</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Record measured quantities with waste allowance, then push them into the estimate — auto-priced from the catalog.</p>
        </div>
        <button data-testid="push-takeoff-btn" onClick={push} disabled={pushing || items.length === 0}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 font-bold hover:bg-orange-600 transition-colors disabled:opacity-50">
          {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightToLine className="h-4 w-4" />} Push to Estimate
        </button>
      </div>

      <div className="inline-flex rounded-lg border border-border p-1 bg-secondary/40">
        <button data-testid="takeoff-view-list" onClick={() => setView("list")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <List className="h-4 w-4" /> Measurements
        </button>
        <button data-testid="takeoff-view-plan" onClick={() => setView("plan")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${view === "plan" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <Map className="h-4 w-4" /> Takeoff from Plan
        </button>
      </div>

      {view === "list" ? (
        <>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              {TYPES.map((t) => (
                <button key={t.id} data-testid={`takeoff-type-${t.id}`} onClick={() => setType(t.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${form.measure_type === t.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-12 gap-2">
              <input data-testid="takeoff-desc-input" value={form.description} onChange={(e) => onDesc(e.target.value)} placeholder="e.g. Interior partition drywall" className="sm:col-span-5 rounded-lg border border-input bg-popover px-3 py-2.5 text-sm outline-none focus:border-primary" />
              <input data-testid="takeoff-qty-input" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="Qty" className="sm:col-span-2 rounded-lg border border-input bg-popover px-3 py-2.5 text-sm font-mono outline-none focus:border-primary" />
              <input data-testid="takeoff-waste-input" type="number" value={form.waste_pct} onChange={(e) => setForm({ ...form, waste_pct: e.target.value })} placeholder="Waste %" className="sm:col-span-2 rounded-lg border border-input bg-popover px-3 py-2.5 text-sm font-mono outline-none focus:border-primary" />
              <input data-testid="takeoff-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Unit" className="sm:col-span-1 rounded-lg border border-input bg-popover px-2 py-2.5 text-sm font-mono text-center outline-none focus:border-primary" />
              <button data-testid="add-takeoff-btn" onClick={add} className="sm:col-span-2 rounded-lg bg-secondary hover:bg-secondary/70 font-semibold text-sm inline-flex items-center justify-center gap-1 py-2.5 transition-colors"><Plus className="h-4 w-4" />Add</button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border py-14 text-center text-muted-foreground">No measurements yet.</div>
          ) : (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]" data-testid="takeoff-table">
                  <thead><tr className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border/60">
                    <th className="px-4 py-2">Description</th><th className="px-2 py-2">Type</th>
                    <th className="px-2 py-2 text-right">Measured</th><th className="px-2 py-2 text-right">Waste</th>
                    <th className="px-2 py-2 text-right">Priced Qty</th><th className="px-2 py-2">Unit</th><th className="px-2 py-2"></th>
                  </tr></thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-b border-border/40" data-testid={`takeoff-row-${it.id}`}>
                        <td className="px-4 py-2.5 font-medium">
                          {it.plan_id && <MapPin className="inline h-3 w-3 text-primary mr-1 -mt-0.5" />}
                          {(it.photo_paths || []).length > 0 && <Camera className="inline h-3 w-3 text-sky-400 mr-1 -mt-0.5" />}
                          {it.description}
                        </td>
                        <td className="px-2 py-2.5 capitalize text-muted-foreground text-xs">{it.measure_type}</td>
                        <td className="px-2 py-2.5 text-right font-mono tabular-nums">{num(it.quantity, 2)}</td>
                        <td className="px-2 py-2.5 text-right font-mono tabular-nums text-xs text-muted-foreground">{it.waste_pct ? `+${num(it.waste_pct, 0)}%` : "—"}</td>
                        <td className="px-2 py-2.5 text-right font-mono tabular-nums font-semibold text-primary" data-testid={`takeoff-priced-${it.id}`}>{num(priced(it), 2)}</td>
                        <td className="px-2 py-2.5 font-mono text-xs">{it.unit}</td>
                        <td className="px-2 py-2.5"><button data-testid={`delete-takeoff-${it.id}`} onClick={() => del(it.id)} className="h-7 w-7 grid place-items-center rounded text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <PlanTakeoff projectId={projectId} items={items} refresh={refresh} onDelete={del} />
      )}
    </div>
  );
}

const MODES = [
  { id: "pin", label: "Pin", icon: MapPin },
  { id: "line", label: "Line", icon: Minus },
  { id: "area", label: "Area", icon: Square },
  { id: "calibrate", label: "Scale", icon: Crosshair },
];

function PlanTakeoff({ projectId, items, refresh, onDelete }) {
  const qc = useQueryClient();
  const inputRef = useRef();
  const photoRef = useRef();
  const imgRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [mode, setMode] = useState("pin");
  const [nat, setNat] = useState(null); // {w,h}
  const [drawStart, setDrawStart] = useState(null); // natural px {px,py}
  const [hover, setHover] = useState(null); // natural px {px,py} for live preview
  const [pending, setPending] = useState(null); // {x,y,geometry,prefill}
  const [pendingCalib, setPendingCalib] = useState(null); // {distPx}
  const [calibForm, setCalibForm] = useState({ length: "", unit: "ft" });
  const [pinForm, setPinForm] = useState({ description: "", measure_type: "count", quantity: "", unit: "ea", waste_pct: "" });
  const [selected, setSelected] = useState(null); // takeoff id
  const [photoBusy, setPhotoBusy] = useState(false);

  const { data: plans = [] } = useQuery({
    queryKey: ["takeoff-plans", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/takeoff-plans`)).data,
  });
  const active = plans.find((p) => p.id === activeId) || plans[0] || null;
  const refreshPlans = () => qc.invalidateQueries({ queryKey: ["takeoff-plans", projectId] });

  const pins = active ? items.filter((it) => it.plan_id === active.id && it.x != null && it.y != null) : [];
  const selPin = pins.find((p) => p.id === selected) || null;

  // running totals grouped by unit
  const totalsByUnit = pins.reduce((acc, p) => { const u = p.unit || "ea"; acc[u] = (acc[u] || 0) + priced(p); return acc; }, {});

  const onFile = async (file) => {
    if (!file) return;
    if (!/(jpe?g|png|webp)$/i.test(file.name)) { toast.error("Use a JPG, PNG or WebP image."); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name.replace(/\.[^.]+$/, "") || "Floor Plan");
    try {
      const { data } = await api.post(`/projects/${projectId}/takeoff-plans/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshPlans();
      setActiveId(data.id);
      toast.success("Plan uploaded — set the scale, then draw or drop pins");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  const toNat = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * nat.w;
    const py = ((e.clientY - rect.top) / rect.height) * nat.h;
    return { px: Math.max(0, Math.min(nat.w, px)), py: Math.max(0, Math.min(nat.h, py)) };
  };
  const natPct = (px, py) => [(px / nat.w) * 100, (py / nat.h) * 100];
  const dist = (a, b) => Math.hypot(a.px - b.px, a.py - b.py);

  const openPinForm = (extra) => {
    setPinForm({ description: "", measure_type: extra?.measure_type || "count", quantity: extra?.quantity ?? "", unit: extra?.unit || "ea", waste_pct: "" });
  };

  const onImgClick = (e) => {
    if (!nat) return;
    const pt = toNat(e);
    if (mode === "pin") {
      const [x, y] = natPct(pt.px, pt.py);
      setPending({ x, y, geometry: null });
      openPinForm({ measure_type: "count", unit: "ea" });
      setSelected(null);
      return;
    }
    if (mode === "calibrate") {
      if (!drawStart) { setDrawStart(pt); return; }
      setPendingCalib({ distPx: dist(drawStart, pt) });
      setCalibForm({ length: "", unit: active.scale_unit || "ft" });
      setDrawStart(null);
      return;
    }
    // line / area require scale
    if (!active.scale) { toast.error("Set the scale first (Scale tool)."); return; }
    if (!drawStart) { setDrawStart(pt); return; }
    if (mode === "line") {
      const d = dist(drawStart, pt) * active.scale;
      const cx = (drawStart.px + pt.px) / 2, cy = (drawStart.py + pt.py) / 2;
      const [x, y] = natPct(cx, cy);
      setPending({ x, y, geometry: { type: "line", points: [[drawStart.px, drawStart.py], [pt.px, pt.py]] } });
      openPinForm({ measure_type: "length", quantity: Math.round(d * 100) / 100, unit: lenUnit(active.scale_unit) });
    } else if (mode === "area") {
      const w = Math.abs(pt.px - drawStart.px) * active.scale;
      const h = Math.abs(pt.py - drawStart.py) * active.scale;
      const area = w * h;
      const x0 = Math.min(drawStart.px, pt.px), y0 = Math.min(drawStart.py, pt.py);
      const x1 = Math.max(drawStart.px, pt.px), y1 = Math.max(drawStart.py, pt.py);
      const [x, y] = natPct((x0 + x1) / 2, (y0 + y1) / 2);
      setPending({ x, y, geometry: { type: "rect", points: [[x0, y0], [x1, y1]] } });
      openPinForm({ measure_type: "area", quantity: Math.round(area * 100) / 100, unit: areaUnit(active.scale_unit) });
    }
    setDrawStart(null);
    setSelected(null);
  };

  const saveCalib = async () => {
    const L = Number(calibForm.length);
    if (!L || L <= 0) { toast.error("Enter the real length"); return; }
    try {
      await api.patch(`/takeoff-plans/${active.id}`, { scale: L / pendingCalib.distPx, scale_unit: calibForm.unit });
      await refreshPlans();
      setPendingCalib(null);
      toast.success("Scale set — you can now draw lines and areas");
      setMode("line");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setPinType = (t) => setPinForm((f) => ({ ...f, measure_type: t, unit: TYPES.find((x) => x.id === t)?.unit || f.unit }));
  const onPinDesc = (v) => setPinForm((f) => {
    const w = f.waste_pct === "" && suggestWaste(v) != null ? String(suggestWaste(v)) : f.waste_pct;
    return { ...f, description: v, waste_pct: w };
  });

  const savePin = async () => {
    if (!pinForm.description.trim()) { toast.error("Enter a description"); return; }
    try {
      await api.post(`/projects/${projectId}/takeoffs`, {
        ...pinForm,
        quantity: Number(pinForm.quantity) || 0,
        waste_pct: Number(pinForm.waste_pct) || 0,
        plan_id: active.id, x: pending.x, y: pending.y, geometry: pending.geometry,
      });
      setPending(null);
      refresh();
      toast.success("Measurement added");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const onPhoto = async (file) => {
    if (!file || !selPin) return;
    setPhotoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/takeoffs/${selPin.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refresh();
      toast.success("Photo attached to pin");
    } catch (e) { toast.error(apiErr(e)); } finally { setPhotoBusy(false); if (photoRef.current) photoRef.current.value = ""; }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="plan-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
        <button data-testid="upload-plan-btn" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-2 rounded-lg bg-secondary hover:bg-secondary/70 px-4 py-2.5 font-semibold text-sm transition-colors disabled:opacity-50">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Floor Plan
        </button>
        {plans.length > 0 && (
          <select data-testid="plan-select" value={active?.id || ""} onChange={(e) => { setActiveId(e.target.value); setDrawStart(null); setSelected(null); }}
            className="rounded-lg border border-input bg-popover px-3 py-2.5 text-sm outline-none focus:border-primary">
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      {!active ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <Map className="h-8 w-8 mx-auto mb-2 opacity-50" />
          Upload a floor-plan image to measure and tag it visually.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card p-3 space-y-3">
          {/* Tool bar */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-border p-1 bg-secondary/40">
              {MODES.map((m) => (
                <button key={m.id} data-testid={`plan-mode-${m.id}`} onClick={() => { setMode(m.id); setDrawStart(null); }}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${mode === m.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <m.icon className="h-3.5 w-3.5" /> {m.label}
                </button>
              ))}
            </div>
            <span className="text-xs text-muted-foreground" data-testid="plan-scale-label">
              {active.scale ? `Scale set (${active.scale_unit})` : "No scale set"}
            </span>
          </div>

          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3 text-primary" />
            {mode === "pin" && "Tap the plan to drop a measurement pin."}
            {mode === "line" && (active.scale ? "Click the start then end point of a length." : "Set the scale first.")}
            {mode === "area" && (active.scale ? "Click two opposite corners of a rectangle." : "Set the scale first.")}
            {mode === "calibrate" && "Click two points a known distance apart, then enter its real length."}
          </p>

          {/* Running totals */}
          {pins.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="plan-running-totals">
              {Object.entries(totalsByUnit).map(([u, v]) => (
                <span key={u} className="text-xs font-mono rounded-full bg-primary/10 text-primary px-2.5 py-1">Σ {num(v, 2)} {u}</span>
              ))}
              <span className="text-xs font-mono rounded-full bg-secondary px-2.5 py-1 text-muted-foreground">{pins.length} pin(s)</span>
            </div>
          )}

          <div className="relative inline-block max-w-full" data-testid="plan-canvas">
            <img ref={imgRef} src={fileUrl(active.path)} alt={active.name} onClick={onImgClick}
              onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
              onMouseMove={(e) => { if (drawStart && nat) setHover(toNat(e)); }}
              onMouseLeave={() => setHover(null)}
              className={`max-w-full max-h-[520px] rounded-lg select-none ${mode === "pin" ? "cursor-crosshair" : "cursor-crosshair"}`} draggable={false} />

            {/* geometry overlay */}
            {nat && (
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${nat.w} ${nat.h}`} preserveAspectRatio="none">
                {pins.filter((p) => p.geometry).map((p) => {
                  const g = p.geometry;
                  if (g.type === "line") return <line key={p.id} x1={g.points[0][0]} y1={g.points[0][1]} x2={g.points[1][0]} y2={g.points[1][1]} stroke="#F97316" strokeWidth={nat.w / 260} strokeLinecap="round" />;
                  if (g.type === "rect") { const [a, b] = g.points; return <rect key={p.id} x={a[0]} y={a[1]} width={b[0] - a[0]} height={b[1] - a[1]} fill="rgba(249,115,22,0.15)" stroke="#F97316" strokeWidth={nat.w / 320} />; }
                  return null;
                })}
                {drawStart && <circle cx={drawStart.px} cy={drawStart.py} r={nat.w / 120} fill="#F59E0B" />}
                {drawStart && hover && (mode === "line" || mode === "calibrate") && (
                  <line x1={drawStart.px} y1={drawStart.py} x2={hover.px} y2={hover.py} stroke="#F59E0B" strokeWidth={nat.w / 280} strokeDasharray={`${nat.w / 90},${nat.w / 90}`} strokeLinecap="round" />
                )}
                {drawStart && hover && mode === "area" && (
                  <rect x={Math.min(drawStart.px, hover.px)} y={Math.min(drawStart.py, hover.py)} width={Math.abs(hover.px - drawStart.px)} height={Math.abs(hover.py - drawStart.py)} fill="rgba(245,158,11,0.12)" stroke="#F59E0B" strokeWidth={nat.w / 320} strokeDasharray={`${nat.w / 90},${nat.w / 90}`} />
                )}
              </svg>
            )}

            {/* pins */}
            {pins.map((p, i) => (
              <button key={p.id} data-testid={`plan-pin-${p.id}`} onClick={(e) => { e.stopPropagation(); setSelected(p.id === selected ? null : p.id); }}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
                <span className={`h-6 w-6 rounded-full text-[11px] font-bold grid place-items-center shadow-lg ring-2 ring-background ${selected === p.id ? "bg-amber-500 text-white" : "bg-primary text-primary-foreground"}`}>{i + 1}</span>
                <span className="mt-0.5 whitespace-nowrap rounded bg-background/90 border border-border px-1 text-[9px] font-mono leading-tight shadow-sm">{num(priced(p), 1)} {p.unit}</span>
              </button>
            ))}

            {/* pending marker */}
            {pending && (
              <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${pending.x}%`, top: `${pending.y}%` }}>
                <div className="h-6 w-6 rounded-full bg-orange-500 text-white grid place-items-center shadow-lg ring-2 ring-background animate-pulse"><MapPin className="h-3.5 w-3.5" /></div>
              </div>
            )}
          </div>

          {/* Calibration form */}
          {pendingCalib && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="calib-form">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm flex items-center gap-1.5"><Crosshair className="h-4 w-4 text-amber-500" /> Set scale — real length of the drawn line</h4>
                <button data-testid="calib-cancel-btn" onClick={() => setPendingCalib(null)} className="h-7 w-7 grid place-items-center rounded hover:bg-secondary text-muted-foreground"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <input data-testid="calib-length-input" type="number" value={calibForm.length} onChange={(e) => setCalibForm({ ...calibForm, length: e.target.value })} placeholder="Length" className="w-28 rounded-lg border border-input bg-popover px-3 py-2.5 text-sm font-mono outline-none focus:border-primary" />
                <select data-testid="calib-unit-select" value={calibForm.unit} onChange={(e) => setCalibForm({ ...calibForm, unit: e.target.value })} className="rounded-lg border border-input bg-popover px-3 py-2.5 text-sm outline-none focus:border-primary">
                  <option value="ft">feet</option><option value="m">meters</option>
                </select>
                <button data-testid="calib-save-btn" onClick={saveCalib} className="rounded-lg bg-amber-500 text-white font-semibold text-sm px-4 py-2.5 hover:bg-amber-600 transition-colors">Set Scale</button>
              </div>
            </div>
          )}

          {/* New measurement form */}
          {pending && (
            <div className="rounded-xl border border-primary/40 bg-primary/5 p-4" data-testid="pin-form">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-sm flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" /> New measurement {pending.geometry ? `(${pending.geometry.type})` : ""}</h4>
                <button data-testid="pin-cancel-btn" onClick={() => setPending(null)} className="h-7 w-7 grid place-items-center rounded hover:bg-secondary text-muted-foreground"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {TYPES.map((t) => (
                  <button key={t.id} data-testid={`pin-type-${t.id}`} onClick={() => setPinType(t.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${pinForm.measure_type === t.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-12 gap-2">
                <input data-testid="pin-desc-input" value={pinForm.description} onChange={(e) => onPinDesc(e.target.value)} placeholder="e.g. North wall drywall" className="sm:col-span-5 rounded-lg border border-input bg-popover px-3 py-2.5 text-sm outline-none focus:border-primary" />
                <input data-testid="pin-qty-input" type="number" value={pinForm.quantity} onChange={(e) => setPinForm({ ...pinForm, quantity: e.target.value })} placeholder="Qty" className="sm:col-span-2 rounded-lg border border-input bg-popover px-3 py-2.5 text-sm font-mono outline-none focus:border-primary" />
                <input data-testid="pin-waste-input" type="number" value={pinForm.waste_pct} onChange={(e) => setPinForm({ ...pinForm, waste_pct: e.target.value })} placeholder="Waste %" className="sm:col-span-2 rounded-lg border border-input bg-popover px-3 py-2.5 text-sm font-mono outline-none focus:border-primary" />
                <input data-testid="pin-unit-input" value={pinForm.unit} onChange={(e) => setPinForm({ ...pinForm, unit: e.target.value })} placeholder="Unit" className="sm:col-span-1 rounded-lg border border-input bg-popover px-2 py-2.5 text-sm font-mono text-center outline-none focus:border-primary" />
                <button data-testid="pin-save-btn" onClick={savePin} className="sm:col-span-2 rounded-lg bg-primary text-primary-foreground font-semibold text-sm inline-flex items-center justify-center gap-1 py-2.5 hover:bg-orange-600 transition-colors"><Plus className="h-4 w-4" />Save</button>
              </div>
            </div>
          )}

          {/* Selected pin detail */}
          {selPin && (
            <div className="rounded-xl border border-border bg-popover p-4" data-testid="pin-detail">
              <input ref={photoRef} type="file" accept="image/*" className="hidden" data-testid="pin-photo-input" onChange={(e) => onPhoto(e.target.files?.[0])} />
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-semibold text-sm flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" /> {selPin.description}</div>
                  <div className="text-xs font-mono text-muted-foreground mt-0.5">{num(priced(selPin), 2)} {selPin.unit}{selPin.waste_pct ? ` (+${num(selPin.waste_pct, 0)}% waste)` : ""}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button data-testid="pin-add-photo-btn" onClick={() => photoRef.current?.click()} disabled={photoBusy}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
                    {photoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Add photo
                  </button>
                  <button data-testid="pin-remove-btn" onClick={() => { onDelete(selPin.id); setSelected(null); }} className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10 transition-colors"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                </div>
              </div>
              {(selPin.photo_paths || []).length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
                  {selPin.photo_paths.map((p, i) => (
                    <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} alt="" className="h-20 w-full object-cover rounded-lg border border-border hover:opacity-80 transition-opacity" /></a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
